import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { PopoverController, PopoverPosition } from "@amzn/stencil-react-components/dist/submodules/popover";
import { Input } from "@amzn/stencil-react-components/input";
import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { IconBookmarkFillSmall, IconBookmarkSmall } from "@amzn/stencil-react-icons";
import { getMyLists, ListType } from "common/context/lists";
import { useLists } from "common/context/lists/ListsContext";
import { getString } from "common/uistringlabels/uiStringUtils";
import { ListModel } from "lib/models/list-models";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { List } from "view/Page/Lists/List";

import { useMessageBannersBox } from "../MessageBannersBox/hook";
import { ListOption } from "./ListOption";

export interface ListActionsProps {
  taxonomyId: string;
  lists: ListModel[];
  allowedDomain: string;
}

export const ListActions: FC<ListActionsProps> = ({ taxonomyId, lists, allowedDomain }) => {
  const { addItemToList, removeItemFromList, createList, creatingFavoriteList, getMyLists } = useLists();
  const [isOpen, setIsOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [processingListIds, setProcessingListIds] = useState<Set<string>>(new Set());
  const [favoriteList, setFavoriteList] = useState<ListModel | undefined>();
  const [isInAnyList, setIsInAnyList] = useState(false);
  const target = useRef(null);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(!isOpen);
  const { addMessageBanner } = useMessageBannersBox();

  useEffect(() => {
    setFavoriteList(lists.find((list) => list.tags?.includes("favorite")));
    setIsInAnyList(lists.some((list) => list.items.some((item) => item.taxonomyId === taxonomyId)));
  }, [lists, taxonomyId]);

  const handleCreateList = async (name?: string, tags?: string[]) => {
    const listName = name || newListName.trim();
    if (!listName || (tags?.includes("favorite") && creatingFavoriteList)) return;
    try {
      await createList(
        {
          name: listName,
          allowedDomains: [allowedDomain],
          itemIds: [taxonomyId],
          ...(tags && { tags }),
        },
        ListType.MY_LISTS
      );
      addMessageBanner(getString("lists.modal.updateSuccess"), MessageBannerType.Success);
      setNewListName("");
    } catch (error) {
      addMessageBanner(getString("lists.modal.updateError"), MessageBannerType.Error);
    }
  };

  const handleToggleList = useCallback(
    async (isInList: boolean, listId: string) => {
      setProcessingListIds((prev) => new Set(prev).add(listId));
      try {
        if (isInList) {
          await removeItemFromList(listId, taxonomyId, ListType.MY_LISTS);
          addMessageBanner(getString("lists.removeItemSuccess"), MessageBannerType.Success);
        } else {
          await addItemToList([taxonomyId], { listId }, ListType.MY_LISTS);
          addMessageBanner(getString("lists.addItemSuccess"), MessageBannerType.Success);
        }
      } catch (error) {
        addMessageBanner(getString("lists.modal.updateError"), MessageBannerType.Error);
      } finally {
        setProcessingListIds((prev) => {
          const next = new Set(prev);
          next.delete(listId);
          return next;
        });
        getMyLists({
          allowedDomains: [allowedDomain],
        });
      }
    },
    [addItemToList, removeItemFromList, taxonomyId, addMessageBanner]
  );

  return (
    <>
      <Button
        icon={isInAnyList ? <IconBookmarkFillSmall /> : <IconBookmarkSmall />}
        variant={ButtonVariant.Tertiary}
        onClick={(e) => {
          toggle();
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{ maxHeight: "40px" }}
        ref={target}
        dataTestId="bookmark-button"
        data-cy="bookmark-button"
      />
      <PopoverController target={target} close={close} isOpen={isOpen} position={PopoverPosition.BottomCenter}>
        <Col
          padding="S300"
          gridGap="S100"
          alignItems="flex-start"
          minWidth="180px"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          dataTestId="popover-content"
        >
          <Col overflow="scroll" maxHeight="140px">
            {favoriteList && (
              <ListOption
                key={`${favoriteList.listId}-${taxonomyId}`}
                index={0}
                isInList={favoriteList.items.some((item) => item.taxonomyId == taxonomyId)}
                list={favoriteList}
                taxonomyId={taxonomyId}
                isProcessing={processingListIds.has(favoriteList.listId)}
                onToggle={handleToggleList}
                data-cy="favorite-list-option"
              />
            )}
            {!favoriteList && (
              <Row alignItems="center" gridGap="S200" flex={1} padding="S100" data-cy="favorite-list-option">
                <Checkbox
                  disabled={creatingFavoriteList}
                  checked={false}
                  onChange={() => handleCreateList(getString("lists.favoriteList"), ["favorite"])}
                />
                <Label>{getString("lists.favoriteList")}</Label>
              </Row>
            )}
            {lists &&
              lists.length > 0 &&
              lists
                .filter((list) => list.allowedDomains.includes(allowedDomain) && !list.tags?.includes("favorite"))
                .map((list, index) => (
                  <ListOption
                    key={`${list.listId}-${taxonomyId}`}
                    index={index + 1}
                    isInList={list.items.some((item) => item.taxonomyId == taxonomyId)}
                    list={list}
                    taxonomyId={taxonomyId}
                    isProcessing={processingListIds.has(list.listId)}
                    onToggle={handleToggleList}
                  />
                ))}
          </Col>
          {(!lists || lists.length === 0 || !lists.some((list) => list.allowedDomains.includes(allowedDomain))) && (
            <Text>{getString("lists.actions.noLists")}</Text>
          )}
          <Spacer height="500" />
          <Col gridGap="S250" width="100%">
            <Input
              placeholder={getString("lists.newList")}
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
            />
            <Button
              variant={ButtonVariant.Secondary}
              onClick={() => handleCreateList()}
              style={{ alignSelf: "center" }}
            >
              {getString("lists.createList")}
            </Button>
          </Col>
        </Col>
      </PopoverController>
    </>
  );
};
